/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * Static-value helpers (#858) shared by the ORTB body editor row, its summary
 * chip, and {@link MappingControl}. Kept pure + unit-tested so the guided view's
 * display and edit stay in sync.
 *
 * Scope note: the guided editor edits **scalars** and **scalar arrays** inline
 * (a comma-separated list — fine for `mimes`, `protocols`, `cat`, `cur`, …).
 * **Complex** arrays/objects (`imp[].pmp.deals`, `user.data`, `source.ext.schain`)
 * are shown read-only with a pointer to Raw JSON — matching the existing `imp`
 * guidance that structural editing belongs in Raw.
 */

import { hasTypographicDoubleQuotes } from './json-normalize';

export function isScalar(v: unknown): boolean {
  return v == null || typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';
}

/** An array whose every element is a scalar (so it round-trips a CSV input). */
export function isScalarArray(v: unknown): v is Array<string | number | boolean | null> {
  return Array.isArray(v) && v.every(isScalar);
}

/** A non-null plain object (a structural value that isn't inline-editable). */
export function isPlainObjectValue(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

/**
 * "Needs a value" — the field is present but nothing meaningful is set: an empty
 * string, null/undefined, an empty array, or an empty object. `0` and `false`
 * are deliberately NOT empty (they're valid OpenRTB values).
 */
export function isEmptyStaticValue(v: unknown): boolean {
  if (v === '' || v == null) return true;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object') return Object.keys(v).length === 0;
  return false;
}

/** One-line display of a static value for a row / summary chip. */
export function formatStaticValue(v: unknown): string {
  if (v == null) return '';
  if (Array.isArray(v)) return isScalarArray(v) ? v.join(', ') : JSON.stringify(v);
  if (typeof v === 'object') return JSON.stringify(v);
  if (typeof v === 'string') return v;
  return typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint' ? String(v) : '';
}

/** Coerce a single CSV token: numeric-looking → number, otherwise the trimmed string. */
function coerceToken(token: string): string | number {
  const t = token.trim();
  return /^-?\d+(?:\.\d+)?$/.test(t) ? Number(t) : t;
}

/**
 * Parse a comma-separated input into a scalar array, coercing numeric tokens so
 * number arrays (`protocols`, `api`) and string arrays (`mimes`, `cat`) both
 * round-trip. Blank tokens are dropped; an empty input yields `[]`.
 */
export function parseScalarArray(raw: string): Array<string | number> {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map(coerceToken);
}

/** Result of parsing a JSON-typed static value from an editor input. */
export interface ParsedJsonValue {
  value?: unknown;
  error?: string;
}

/**
 * Parse a structured static value typed as JSON. Accepts a bare object/array
 * body (`{ "foo": "bar" }`, `[1, 2]`); anything that isn't valid JSON reports an
 * error so the caller can hold the previous value rather than emit garbage.
 */
export function parseJsonValue(raw: string): ParsedJsonValue {
  const trimmed = raw.trim();
  if (!trimmed) return { value: {} };
  try {
    return { value: JSON.parse(trimmed) };
  } catch (err) {
    const base = err instanceof Error ? err.message : 'Invalid JSON';
    // Lead with the likely cause when a paste brought curly quotes with it.
    if (hasTypographicDoubleQuotes(trimmed)) {
      return { error: `${base} — this looks like curly quotes (“ ”); JSON needs straight quotes (").` };
    }
    return { error: base };
  }
}

// ---------------------------------------------------------------------------
// Send as — typing a STATIC value (#923)
// ---------------------------------------------------------------------------

/**
 * A static value's "Send as" needs no `$valueType` on the wire, because the ORTB body
 * **is JSON**: `210` and `"210"` are already different values there. So the type is
 * applied at authoring time and the stored body is literally correct.
 *
 * That is the whole gap this closes. The scalar input produced `e.target.value`,
 * always a string, so a `bidfloor` of `2.5` or an `ext.number_of_ads` of `6` shipped
 * as `"2.5"` and `"6"` — a type violation on a numeric OpenRTB field, and one the
 * operator had no control over: the expression and path tabs had a Send-as select
 * and the static tab did not.
 */

/** The type a stored static value currently *is* — what the Send-as select shows. */
export function staticValueType(v: unknown): 'string' | 'number' | 'boolean' | undefined {
  if (typeof v === 'number') return 'number';
  if (typeof v === 'boolean') return 'boolean';
  if (typeof v === 'string') return 'string';
  return undefined;
}

/**
 * Coerce raw editor text to the chosen type.
 *
 * Returns the **trimmed string** when the text cannot be the requested type, rather
 * than `NaN` or a silent `false`: mid-typing states ("", "-", "2.") must not destroy
 * what the operator is writing, and the row's own validation is what tells them the
 * value is not yet a number. This differs deliberately from the driver's
 * `coerceScalar`, which omits an uncoercible value — by then there is no operator to
 * correct it, and a wrong type on the wire is worse than an absent key.
 */
export function coerceStaticValue(raw: string, asType?: 'string' | 'number' | 'boolean'): string | number | boolean {
  const trimmed = raw.trim();
  if (asType === 'number') {
    if (trimmed === '') return '';
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : trimmed;
  }
  if (asType === 'boolean') {
    const lower = trimmed.toLowerCase();
    if (lower === 'true' || lower === '1' || lower === 'yes') return true;
    if (lower === 'false' || lower === '0' || lower === 'no') return false;
    return trimmed;
  }
  return raw;
}
